import { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Download, X, WifiOff } from 'lucide-react';
import { onConnectionChange, isOnline, getQueueLength, flushQueue } from '../../lib/pwa';
import { api } from '../../lib/supabase';

const DISMISS_KEY = 'rivl:pwa-dismissed';

export function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [shown, setShown] = useState(false);
  const [installed, setInstalled] = useState(
    typeof window !== 'undefined' && window.matchMedia?.('(display-mode: standalone)').matches
  );
  const [online, setOnline] = useState(isOnline());
  const [queueLen, setQueueLen] = useState(getQueueLength());

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      if (localStorage.getItem(DISMISS_KEY)) return;
      setDeferred(e);
      setShown(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', () => {
      setInstalled(true);
      setShown(false);
    });
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  useEffect(() => {
    return onConnectionChange(async (next) => {
      setOnline(next);
      if (next) {
        // Reconnected — replay queued operations
        const res = await flushQueue({
          checkin: ({ eventId }) => api.checkIn(eventId),
        });
        setQueueLen(getQueueLength());
        if (res.flushed > 0) {
          // Use console rather than toast to avoid double-importing sonner here
          console.info(`[RIVL] Replayed ${res.flushed} offline check-ins`);
        }
      }
    });
  }, []);

  // Poll queue length cheaply (in case the user just queued from another component)
  useEffect(() => {
    const i = setInterval(() => setQueueLen(getQueueLength()), 4000);
    return () => clearInterval(i);
  }, []);

  async function install() {
    if (!deferred) return;
    deferred.prompt();
    const result = await deferred.userChoice;
    if (result.outcome === 'accepted') setInstalled(true);
    setShown(false);
    setDeferred(null);
  }

  function dismiss() {
    setShown(false);
    localStorage.setItem(DISMISS_KEY, '1');
  }

  return (
    <>
      {!online && (
        <div className="fixed top-0 inset-x-0 z-50 bg-destructive text-destructive-foreground py-2 px-4 text-center text-xs font-mono flex items-center justify-center gap-2" data-testid="offline-banner">
          <WifiOff className="h-3.5 w-3.5" />
          Offline — actions are queued{queueLen > 0 ? ` (${queueLen} pending)` : ''}
        </div>
      )}

      {shown && !installed && (
        <div
          className="fixed left-4 right-4 bottom-20 z-40 rivl-card p-4 flex items-center gap-3 shadow-lg bg-background"
          data-testid="pwa-install-prompt"
        >
          <div className="h-10 w-10 rounded-md bg-primary text-primary-foreground flex items-center justify-center">
            <Download className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <div className="text-sm font-semibold">Install RIVL</div>
            <div className="text-[11px] text-muted-foreground">Run from your home screen, even offline.</div>
          </div>
          <Button onClick={install} size="sm" className="h-9 rounded-md" data-testid="pwa-install-btn">
            Install
          </Button>
          <button
            onClick={dismiss}
            className="h-8 w-8 rounded-md hover:bg-secondary flex items-center justify-center"
            data-testid="pwa-install-dismiss"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
    </>
  );
}
